import { useState, useEffect, useCallback } from "react";
import {
  Box, Grid, TextField, Button, Typography, IconButton,
  Checkbox, FormControlLabel, Divider, Chip, InputAdornment,
  Paper, Collapse, MenuItem, ToggleButton, ToggleButtonGroup,
  Alert, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogContent,
  DialogTitle, LinearProgress
} from "@mui/material";
import CloseIcon              from "@mui/icons-material/Close";
import PersonIcon             from "@mui/icons-material/Person";
import CurrencyRupeeIcon      from "@mui/icons-material/CurrencyRupee";
import PaymentIcon            from "@mui/icons-material/Payment";
import SwapHorizIcon          from "@mui/icons-material/SwapHoriz";
import CardGiftcardIcon       from "@mui/icons-material/CardGiftcard";
import ReceiptIcon            from "@mui/icons-material/Receipt";
import AccountBalanceIcon     from "@mui/icons-material/AccountBalance";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PendingActionsIcon     from "@mui/icons-material/PendingActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ElectricRickshawIcon   from "@mui/icons-material/ElectricRickshaw";
import TwoWheelerIcon         from "@mui/icons-material/TwoWheeler";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import PowerIcon              from "@mui/icons-material/Power";
import SearchIcon             from "@mui/icons-material/Search";
import VisibilityIcon         from "@mui/icons-material/Visibility";
import AddCircleOutlineIcon   from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon      from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon        from "@mui/icons-material/CheckCircle";
import api                    from "../service/api";

/* ═══════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════ */
const EV_LOCATIONS      = ["Chaitanyapur", "Mahisadal", "Boroda"];
const FINANCE_COMPANIES = ["Bajaj Finance Limited", "HP Akasa Finance Limited"];
const EV_VEHICLE_TYPES  = ["E_RICKSHAW", "SCOOTY"];
const getDraftKey       = (id) => `ev_sell_draft_${id}`;
const fmtMoney          = (v) => v != null ? `₹${Number(v).toLocaleString("en-IN")}` : "—";

/* ═══════════════════════════════════════════════
   SECTION HEADER
═══════════════════════════════════════════════ */
const Section = ({ icon, title, color = "#1976d2" }) => (
  <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle2" fontWeight={700} letterSpacing={1}
      sx={{ color, textTransform: "uppercase", fontSize: "0.72rem" }}>
      {title}
    </Typography>
    <Box flex={1} height="1px" bgcolor={`${color}33`} ml={1} />
  </Box>
);

/* ═══════════════════════════════════════════════
   GENERIC FIELD WRAPPER
═══════════════════════════════════════════════ */
const F = ({
  label, name, value, onChange, error, helperText,
  type = "text", required, xs = 12, md = 6,
  startAdornment, multiline, rows, select, children, disabled
}) => (
  <Grid item xs={xs} md={md}>
    <TextField
      fullWidth size="small" label={label} name={name}
      value={value ?? ""} onChange={onChange} type={type}
      required={required} error={!!error}
      helperText={error || helperText || ""}
      multiline={multiline} rows={rows} select={select} disabled={disabled}
      InputLabelProps={type === "date" ? { shrink: true } : undefined}
      InputProps={startAdornment
        ? { startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment> }
        : undefined}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
    >
      {select && children}
    </TextField>
  </Grid>
);

const rupee   = <CurrencyRupeeIcon sx={{ fontSize: 14 }} />;
const qtyIcon = <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />;

/* ═══════════════════════════════════════════════
   STOCK SEARCH + TABLE
═══════════════════════════════════════════════ */
const StockSearchTable = ({ stockType, onSelect }) => {
  const [query,       setQuery]       = useState("");
  const [results,     setResults]     = useState([]);
  const [searching,   setSearching]   = useState(false);
  const [err,         setErr]         = useState("");
  const [viewItem,    setViewItem]    = useState(null);
  const [viewData,    setViewData]    = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true); setErr("");
    try {
      const { data } = await api.get("/filter-battery-stock", {
        params: { serialNo: q.trim(), stockType }
      });
      setResults(data?.data || data || []);
    } catch {
      setErr("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  }, [stockType]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), 380);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const handleView = async (item) => {
    setViewItem(item); setViewData(null); setViewLoading(true);
    try {
      const { data } = await api.get(`/get-specific-battery-stock/${item._id}`);
      setViewData(data?.data || data);
    } catch {
      setViewData({ _err: "Failed to load." });
    } finally {
      setViewLoading(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN") : "—";

  return (
    <Box>
      <TextField
        fullWidth size="small" autoFocus
        placeholder={`Search ${stockType === "BATTERY" ? "battery" : "charger"} by serial no…`}
        value={query} onChange={e => setQuery(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#90a4ae" }} /></InputAdornment>,
          endAdornment:   searching ? <CircularProgress size={14} /> : null
        }}
        sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
      />

      {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}

      {!searching && !query.trim() && (
        <Box py={2} textAlign="center">
          <Typography color="text.secondary" fontSize={12}>Type a serial number to search…</Typography>
        </Box>
      )}
      {!searching && query.trim() && results.length === 0 && (
        <Box py={2} textAlign="center">
          <Typography color="text.secondary" fontSize={12}>
            No {stockType === "BATTERY" ? "batteries" : "chargers"} found for "<strong>{query}</strong>"
          </Typography>
        </Box>
      )}

      {results.length > 0 && (
        <TableContainer component={Paper} variant="outlined"
          sx={{ borderRadius: 2, maxHeight: 260, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 11, bgcolor: "#f5f7fa", color: "#546e7a", py: 0.8 } }}>
                <TableCell>Sl no.</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Company / Model</TableCell>
                <TableCell>Serial no.</TableCell>
                <TableCell>Battery type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Purchase</TableCell>
                <TableCell>Sale price</TableCell>
                <TableCell>Warranty</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((item) => (
                <TableRow key={item._id} hover sx={{ "&:hover": { bgcolor: "#f8faff" }, cursor: "pointer" }}>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>{item.slNo}</TableCell>
                  <TableCell>
                    <Chip
                      icon={item.stockType === "BATTERY"
                        ? <BatteryChargingFullIcon sx={{ fontSize: 11 }} />
                        : <PowerIcon sx={{ fontSize: 11 }} />}
                      label={item.stockType === "BATTERY" ? "Battery" : "Charger"}
                      size="small"
                      sx={{
                        bgcolor:    item.stockType === "BATTERY" ? "#f3e5f5" : "#e8f5e9",
                        color:      item.stockType === "BATTERY" ? "#7b1fa2" : "#2e7d32",
                        border:     `1px solid ${item.stockType === "BATTERY" ? "#ce93d8" : "#a5d6a7"}`,
                        fontWeight: 600, fontSize: 10
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    <Typography fontWeight={700} fontSize={12}>{item.companyName || "—"}</Typography>
                    <Typography fontSize={11} color="text.secondary">{item.model || "—"}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: "monospace", letterSpacing: 0.5 }}>
                    {item.serialNo || "N/A"}
                  </TableCell>
                  <TableCell>
                    {item.batteryType
                      ? <Chip label={item.batteryType} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                      : <Typography fontSize={12} color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{item.location}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{fmtMoney(item.purchasePrice)}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "#2e7d32" }}>{fmtMoney(item.salePrice)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{item.warrantyDate ? `${item.warrantyDate} Mo` : "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.status === "IN_STOCK" ? "In stock" : item.status}
                      size="small"
                      sx={{
                        bgcolor:    item.status === "IN_STOCK" ? "#e8f5e9" : "#fce4ec",
                        color:      item.status === "IN_STOCK" ? "#2e7d32" : "#c62828",
                        border:     `1px solid ${item.status === "IN_STOCK" ? "#a5d6a7" : "#ef9a9a"}`,
                        fontWeight: 700, fontSize: 10
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} alignItems="center">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(item); }}
                        sx={{ color: "#546e7a", "&:hover": { color: "#1976d2" } }}>
                        <VisibilityIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      {item.status === "IN_STOCK" && (
                        <Button size="small" variant="contained"
                          onClick={(e) => { e.stopPropagation(); onSelect(item); }}
                          sx={{
                            bgcolor: "#2e7d32", fontSize: 10, fontWeight: 700,
                            px: 1, py: 0.3, borderRadius: 1.5, minWidth: 0,
                            "&:hover": { bgcolor: "#1b5e20" }
                          }}>
                          SELECT
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!viewItem} onClose={() => { setViewItem(null); setViewData(null); }}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography fontWeight={700} fontSize={14}>
            {viewItem?.stockType === "BATTERY" ? "🔋 Battery" : "⚡ Charger"} — Stock Detail
          </Typography>
          <IconButton size="small" onClick={() => { setViewItem(null); setViewData(null); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 1.5 }}>
          {viewLoading && <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>}
          {viewData?._err && <Alert severity="error">{viewData._err}</Alert>}
          {viewData && !viewData._err && (
            <Box>
              {[
                ["Serial No.",     viewData.serialNo,   true],
                ["Company",        viewData.companyName],
                ["Model",          viewData.model],
                ["Battery Type",   viewData.batteryType],
                ["Location",       viewData.location],
                ["Status",         viewData.status],
                ["Purchase Price", fmtMoney(viewData.purchasePrice)],
                ["Sale Price",     fmtMoney(viewData.salePrice)],
                ["Warranty",       viewData.warrantyDate ? `${viewData.warrantyDate} Months` : null],
                ["Invoice No.",    viewData.invoiceNo],
                ["Remarks",        viewData.remarks],
                ["Entry Date",     fmtDate(viewData.entryDate)],
              ].filter(([, v]) => v).map(([label, val, mono]) => (
                <Box key={label} display="flex" justifyContent="space-between" py={0.6}
                  sx={{ borderBottom: "0.5px solid #f0f0f0", "&:last-child": { borderBottom: "none" } }}>
                  <Typography fontSize={12} color="text.secondary">{label}</Typography>
                  <Typography fontSize={12} fontWeight={600} fontFamily={mono ? "monospace" : undefined}>{val}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════════════════════════════════
   BATTERY / CHARGER INLINE SELL FIELDS
═══════════════════════════════════════════════ */
const SubSaleFields = ({ stockItem, stockType, subForm, onChange, errors }) => {
  const toggle = (key) => onChange({ target: { name: key, value: !subForm[key] } });

  return (
    <Box>
      <Paper variant="outlined" sx={{
        p: 1.2, mb: 1.5, borderRadius: 1.5, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center",
        bgcolor:     stockType === "BATTERY" ? "#f3e5f5" : "#e8f5e9",
        borderColor: stockType === "BATTERY" ? "#ce93d8" : "#a5d6a7",
      }}>
        {stockType === "BATTERY"
          ? <BatteryChargingFullIcon sx={{ color: "#7b1fa2", fontSize: 17 }} />
          : <PowerIcon sx={{ color: "#2e7d32", fontSize: 17 }} />}
        <Typography fontWeight={700} fontSize={12}>{stockItem.companyName} — {stockItem.model}</Typography>
        <Chip label={`S/N: ${stockItem.serialNo || "N/A"}`} size="small" variant="outlined" sx={{ fontSize: 10 }} />
        <Chip label={stockItem.location} size="small" sx={{ fontSize: 10 }} />
        <Chip label={fmtMoney(stockItem.salePrice)} size="small" color="success" variant="outlined" sx={{ fontSize: 10 }} />
      </Paper>

      <Grid container spacing={1.5}>
        <F label="Sale Price *" name="salePrice" value={subForm.salePrice} onChange={onChange} error={errors?.salePrice} required type="number" startAdornment={rupee} md={4} />
        <F label="ORP *"        name="orp"       value={subForm.orp}       onChange={onChange} error={errors?.orp}       required type="number" startAdornment={rupee} md={4} />
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="Sale Date" name="saleDate" type="date"
            value={subForm.saleDate} onChange={onChange} InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} />
        </Grid>
        <F label="Discount (₹)" name="discount" value={subForm.discount} onChange={onChange} type="number" startAdornment={rupee} md={4} />

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ px: 1.5, py: 0.8, borderRadius: 1.5, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControlLabel
              control={<Checkbox checked={!!subForm.isCash}    onChange={() => toggle("isCash")}    size="small" sx={{ color: "#2e7d32" }} />}
              label={<Typography variant="body2" fontWeight={600} color="#2e7d32" fontSize={12}>CASH</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={!!subForm.isFinance} onChange={() => toggle("isFinance")} size="small" sx={{ color: "#1565c0" }} />}
              label={<Typography variant="body2" fontWeight={600} color="#1565c0" fontSize={12}>FINANCE</Typography>}
            />
          </Paper>
        </Grid>

        <Collapse in={!!subForm.isCash} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <F label="Cash Amount *" name="cashAmount" value={subForm.cashAmount} onChange={onChange}
              error={errors?.cashAmount} required type="number" startAdornment={rupee} md={6} />
          </Grid>
        </Collapse>

        <Collapse in={!!subForm.isFinance} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Finance Company *" name="financeCompany"
                value={subForm.financeCompany} onChange={onChange}
                error={!!errors?.financeCompany} helperText={errors?.financeCompany || ""}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>
                {FINANCE_COMPANIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
            </Grid>
            <F label="Finance Amount *" name="financeAmount" value={subForm.financeAmount} onChange={onChange}
              error={errors?.financeAmount} required type="number" startAdornment={rupee} md={4} />
             <F label="finance Down payment*" name="financeDown" value={subForm.financeDown} onChange={onChange}
              error={errors?.financeDown} required type="number" startAdornment={rupee} md={4} />
           
            
            <F label="Finance Months"   name="financeMonths" value={subForm.financeMonths} onChange={onChange} type="number" md={4} />
            {/* <F label="EMI Amount"   name="emiAmount" value={subForm.emiAmount} onChange={onChange} type="number" md={4} /> */}
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Finance Date" name="financeDate" type="date"
                value={subForm.financeDate} onChange={onChange} InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} />
            </Grid>
          </Grid>
        </Collapse>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{
            px: 1.5, py: 1, borderRadius: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
            bgcolor:     subForm.dueStatus === "DUE" ? "#fff8f5" : "#f1f8e9",
            borderColor: subForm.dueStatus === "DUE" ? "#ff7043" : "#66bb6a",
          }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>Due:</Typography>
            <ToggleButtonGroup value={subForm.dueStatus} exclusive size="small"
              onChange={(_, v) => v && onChange({ target: { name: "dueStatus", value: v } })}
              sx={{ "& .MuiToggleButton-root": { px: 2, py: 0.4, fontWeight: 700, fontSize: "0.7rem", borderRadius: "6px !important", border: "1px solid", mx: 0.4 } }}>
              <ToggleButton value="PAID"
                sx={{ borderColor: "#66bb6a !important", "&.Mui-selected": { bgcolor: "#2e7d32 !important", color: "#fff" } }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} /> PAID
              </ToggleButton>
              <ToggleButton value="DUE"
                sx={{ borderColor: "#ff7043 !important", "&.Mui-selected": { bgcolor: "#e65100 !important", color: "#fff" } }}>
                <PendingActionsIcon sx={{ fontSize: 13, mr: 0.4 }} /> DUE
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Grid>
        <Collapse in={subForm.dueStatus === "DUE"} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0.3 }}>
            <F label="Due Amount *" name="dueAmount"  value={subForm.dueAmount}  onChange={onChange}
              error={errors?.dueAmount} required type="number" startAdornment={rupee} md={6} />
            <F label="Due Remarks"  name="DueRemarks" value={subForm.DueRemarks} onChange={onChange} md={6} />
          </Grid>
        </Collapse>

        <F label="Remarks" name="remarks" value={subForm.remarks} onChange={onChange} xs={12} md={12} />
      </Grid>
    </Box>
  );
};

/* ═══════════════════════════════════════════════
   DEFAULT SUB-FORM STATE
═══════════════════════════════════════════════ */
const makeSubForm = (stockItem) => ({
  salePrice:      String(stockItem?.salePrice || ""),
  orp:            String(stockItem?.salePrice || ""),
  saleDate:       new Date().toISOString().split("T")[0],
  discount:       "",
  isCash:         true,
  cashAmount:     "",
  isFinance:      false,
  financeCompany: "",
  financeAmount:  "",
  financeDown:"",
  financeMonths:  "",
  emiAmount:"",
  financeDate:    "",
  dueStatus:      "PAID",
  dueAmount:      "",
  DueRemarks:     "",
  remarks:        "",
});

/* ═══════════════════════════════════════════════
   VALIDATE SUB-FORM
═══════════════════════════════════════════════ */
const validateSubForm = (f, label) => {
  const e = {};
  if (!f.salePrice) e.salePrice = `${label}: sale price required`;
  if (!f.orp)       e.orp       = `${label}: ORP required`;
  if (f.isCash    && !f.cashAmount)    e.cashAmount     = `${label}: cash amount required`;
  if (f.isFinance && !f.financeCompany) e.financeCompany = `${label}: finance company required`;
  if (f.isFinance && !f.financeAmount)  e.financeAmount  = `${label}: finance amount required`;
  if (f.isFinance && !f.financeDown)  e.financeDown  = `${label}: finance Down amount required`;
  
  if (f.dueStatus === "DUE" && !f.dueAmount) e.dueAmount = `${label}: due amount required`;
  return e;
};

/* ═══════════════════════════════════════════════
   SELL STATUS STEP INDICATOR
═══════════════════════════════════════════════ */
const SellProgress = ({ steps }) => (
  <Box mb={2}>
    {steps.map((s, i) => (
      <Box key={i} display="flex" alignItems="center" gap={1.5} py={0.6}>
        {s.status === "pending"  && <Box sx={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid #b0bec5", bgcolor: "#fff" }} />}
        {s.status === "running"  && <CircularProgress size={20} thickness={5} />}
        {s.status === "done"     && <CheckCircleIcon sx={{ fontSize: 20, color: "#2e7d32" }} />}
        {s.status === "error"    && <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#ef5350", display: "flex", alignItems: "center", justifyContent: "center" }}><CloseIcon sx={{ fontSize: 13, color: "#fff" }} /></Box>}
        <Typography fontSize={13} fontWeight={s.status === "running" ? 700 : 500}
          color={s.status === "done" ? "#2e7d32" : s.status === "error" ? "#c62828" : s.status === "running" ? "#1565c0" : "text.secondary"}>
          {s.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

/* ═══════════════════════════════════════════════
   DRAFT SERIALIZATION HELPERS
   batteries/charger sub-forms stored by stockItem._id
   so on restore we can show what was selected.
═══════════════════════════════════════════════ */
const serializeDraft = (form, batteries, charger) => ({
  form,
  batteries: batteries.map(b => ({ id: b.id, stockItem: b.stockItem, subForm: b.subForm })),
  charger:   charger ? { stockItem: charger.stockItem, subForm: charger.subForm } : null,
});

const deserializeDraft = (raw) => {
  try {
    const p = JSON.parse(raw);
    return {
      form:      p.form      || null,
      batteries: Array.isArray(p.batteries) ? p.batteries : [],
      charger:   p.charger   || null,
    };
  } catch {
    return null;
  }
};

/* ═══════════════════════════════════════════════
   MAIN FORM
═══════════════════════════════════════════════ */
const SellBikeForm = ({ bike, onClose, onSuccess }) => {
  const today    = new Date().toISOString().split("T")[0];
  const draftKey = getDraftKey(bike?._id);

  const defaultExchange = {
    engine: "", chasis: "", registrationNumber: "", model: "",
    ownerName: "", phone: "", location: "", vehicleType: "",
    companyName: "", colour: "", batteryType: "", purchasePrice: "", remarks: "",
  };

  const makeDefaultForm = () => ({
    evStockId:     bike?._id || "",
    customerName:  "", fatherName: "",
    mobile:        "", mobile2: "",
    address:       "", village: "", postOffice: "",
    policeStation: "", pinCode: "", landmark: "", GSTIN: "",
    salePrice:     "", orp: "",
    saleDate:      today, location: bike?.location || "", noPlate: "",
    isCash:        true, isFinance: false, isExchange: false,
    cashAmount:    "",
    financeCompany: "", financeAmount: "",financeDown:"", financeMonths: "", emiAmount: "", financeDate: "",
    exchange:      { ...defaultExchange },
    helmet: "", seatCover: "", bikeCover: "",
    transport: "", tcr: "", regBroker: "",
    giftName: "", giftQty: "0",
    rto: "", insurance: "",
    discount: "", couponCashback: "",helpBattery:false,helpBatterySerial:"",
    dueStatus: "PAID", dueAmount: "", dueRemarks: "",
    remarks: "",
  });

  // ── Restore from draft or start fresh ──────────────────────────────────────
  const [form,       setForm]       = useState(() => {
    const draft = deserializeDraft(localStorage.getItem(draftKey));
    if (draft?.form) return { ...makeDefaultForm(), ...draft.form, exchange: { ...defaultExchange, ...(draft.form.exchange || {}) } };
    return makeDefaultForm();
  });

  const [batteries,  setBatteries]  = useState(() => {
    const draft = deserializeDraft(localStorage.getItem(draftKey));
    return draft?.batteries || [];
  });

  const [charger,    setCharger]    = useState(() => {
    const draft = deserializeDraft(localStorage.getItem(draftKey));
    return draft?.charger || null;
  });

  const [errors,      setErrors]      = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [steps,       setSteps]       = useState([]);
  const [hasDraft,    setHasDraft]    = useState(false);
  const [subErrors,   setSubErrors]   = useState({});
  const [batSearchOpen,  setBatSearchOpen]  = useState(false);
  const [charSearchOpen, setCharSearchOpen] = useState(false);

  // Detect if there's an existing draft on mount
  useEffect(() => {
    try { if (localStorage.getItem(draftKey)) setHasDraft(true); } catch { /* */ }
  }, [draftKey]);

  // Persist entire draft (form + batteries + charger) on every change
  useEffect(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify(serializeDraft(form, batteries, charger)));
    } catch { /* */ }
  }, [form, batteries, charger, draftKey]);

  const clearDraft = () => {
    localStorage.removeItem(draftKey);
    setHasDraft(false);
    setForm(makeDefaultForm());
    setBatteries([]);
    setCharger(null);
  };

  const orpNum     = Number(form.orp            || 0);
  const discNum    = Number(form.discount       || 0);
  const couponNum  = Number(form.couponCashback || 0);
  const netDisplay = orpNum - discNum - couponNum;

  /* ── Form field handlers ────────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => { const n = { ...p }; delete n[name]; return n; });
  };

  const handleExchange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, exchange: { ...p.exchange, [name]: value } }));
    if (errors[`ex_${name}`]) setErrors(p => { const n = { ...p }; delete n[`ex_${name}`]; return n; });
  };

  const toggle = (key) => setForm(p => ({ ...p, [key]: !p[key] }));

  /* ── Battery slot handlers ──────────────────────────────────────────────── */
  const addBatterySlot = (stockItem) => {
    const id = `${Date.now()}`;
    setBatteries(prev => [...prev, { id, stockItem, subForm: makeSubForm(stockItem) }]);
    setBatSearchOpen(false);
    setSubErrors(p => { const n = { ...p }; delete n[`bat_${id}`]; return n; });
  };

  const removeBattery = (id) => {
    setBatteries(prev => prev.filter(b => b.id !== id));
    setSubErrors(p => { const n = { ...p }; delete n[`bat_${id}`]; return n; });
  };

  const updateBatSubForm = (id, e) => {
    const { name, value } = e.target;
    setBatteries(prev => prev.map(b =>
      b.id === id ? { ...b, subForm: { ...b.subForm, [name]: value } } : b
    ));
    setSubErrors(p => {
      const key = `bat_${id}`;
      if (!p[key]) return p;
      const n = { ...p, [key]: { ...p[key] } };
      delete n[key][name]; return n;
    });
  };

  /* ── Charger handlers ───────────────────────────────────────────────────── */
  const addCharger = (stockItem) => {
    setCharger({ stockItem, subForm: makeSubForm(stockItem) });
    setCharSearchOpen(false);
    setSubErrors(p => { const n = { ...p }; delete n.charger; return n; });
  };

  const removeCharger = () => {
    setCharger(null);
    setSubErrors(p => { const n = { ...p }; delete n.charger; return n; });
  };

  const updateCharSubForm = (e) => {
    const { name, value } = e.target;
    setCharger(prev => prev ? { ...prev, subForm: { ...prev.subForm, [name]: value } } : prev);
    setSubErrors(p => {
      if (!p.charger) return p;
      const n = { ...p, charger: { ...p.charger } };
      delete n.charger[name]; return n;
    });
  };

  /* ── EV form validation ─────────────────────────────────────────────────── */
  const validateEV = () => {
    const e = {};
    if (!String(form.customerName || "").trim()) e.customerName = "Required";
    if (!String(form.mobile       || "").trim()) e.mobile       = "Required";
    if (!form.salePrice) e.salePrice = "Required";
    if (!form.orp)       e.orp       = "Required";
    if (!form.saleDate)  e.saleDate  = "Required";
    if (!form.location)  e.location  = "Required";
    if (form.isCash    && !form.cashAmount)     e.cashAmount     = "Required";
    if (form.isFinance && !form.financeCompany) e.financeCompany = "Required";
    if (form.isFinance && !form.financeAmount)  e.financeAmount  = "Required";
    if (form.isFinance && !form.financeDown)  e.financeDown  = "Required";
     if (form.isExchange) {
      if (!String(form.exchange?.engine             || "").trim()) e.ex_engine             = "Required";
      if (!String(form.exchange?.chasis             || "").trim()) e.ex_chasis             = "Required";
      if (!String(form.exchange?.registrationNumber || "").trim()) e.ex_registrationNumber = "Required";
      if (!String(form.exchange?.model              || "").trim()) e.ex_model              = "Required";
    }
    if (form.dueStatus === "DUE" && !form.dueAmount) e.dueAmount = "Required";
    return e;
  };

  /* ══════════════════════════════════════════════════════════════
     MAIN SUBMIT — Single API call to /sell-bike-battery-charger
  ══════════════════════════════════════════════════════════════ */
  const handleSubmit = async () => {
    // 1. Validate EV fields
    const evErr = validateEV();

    // 2. Validate each battery sub-form
    const newSubErrors = {};
    batteries.forEach(b => {
      const e = validateSubForm(b.subForm, `Battery (${b.stockItem.serialNo || b.stockItem.model})`);
      if (Object.keys(e).length) newSubErrors[`bat_${b.id}`] = e;
    });

    // 3. Validate charger sub-form
    if (charger) {
      const e = validateSubForm(charger.subForm, `Charger (${charger.stockItem.serialNo || charger.stockItem.model})`);
      if (Object.keys(e).length) newSubErrors.charger = e;
    }

    setErrors(evErr);
    setSubErrors(newSubErrors);

    if (Object.keys(evErr).length || Object.keys(newSubErrors).length) {
      const allMsgs = [
        ...Object.values(evErr),
        ...Object.values(newSubErrors).flatMap(e => Object.values(e))
      ];
      setErrors(p => ({ ...p, _summary: allMsgs }));
      return;
    }

    setSubmitting(true);
    setErrors({});

    // Show single progress step (one API call now)
    setSteps([{ label: "Submitting sale (EV + batteries + charger)…", status: "running", key: "main" }]);

    const mobiles = [form.mobile.trim(), form.mobile2?.trim()].filter(Boolean);

    /* ── Build batteries array for API ─────────────────────────────── */
    const batteriesPayload = batteries.map(b => {
      const f = b.subForm;
      const hasFinB = !!(f.isFinance && f.financeAmount);
      return {
        batteryStockId: b.stockItem._id,
        salePrice:      Number(f.salePrice),
        orp:            Number(f.orp),
        saleDate:       f.saleDate,
        cashAmount:     f.isCash ? Number(f.cashAmount) : 0,
        finance: hasFinB ? {
          companyName: f.financeCompany,
          amount:      Number(f.financeAmount),
          financeDown: Number(f.financeDown),
          months:      Number(f.financeMonths || 0),
          emiAmount:   Number(f.emiAmount || 0),
          date:        f.financeDate || undefined,
        } : undefined,
        dueAmount:  f.dueStatus === "DUE" ? Number(f.dueAmount) : 0,
        DueRemarks: f.dueStatus === "DUE" ? (f.DueRemarks || "") : "",
        discount:   Number(f.discount || 0),
        remarks:    f.remarks,
      };
    });

    /* ── Build charger object for API ───────────────────────────────── */
    let chargerPayload;
    if (charger) {
      const f = charger.subForm;
      const hasFinC = !!(f.isFinance && f.financeAmount);
      chargerPayload = {
        chargerStockId: charger.stockItem._id,
        salePrice:      Number(f.salePrice),
        orp:            Number(f.orp),
        saleDate:       f.saleDate,
        cashAmount:     f.isCash ? Number(f.cashAmount) : 0,
        finance: hasFinC ? {
          companyName: f.financeCompany,
          amount:      Number(f.financeAmount),
          financeDown:   Number(f.financeDown),
          months:      Number(f.financeMonths || 0),
          emiAmount:   Number(f.emiAmount || 0),
          date:        f.financeDate || undefined,
        } : undefined,
        dueAmount:  f.dueStatus === "DUE" ? Number(f.dueAmount) : 0,
        DueRemarks: f.dueStatus === "DUE" ? (f.DueRemarks || "") : "",
        discount:   Number(f.discount || 0),
        remarks:    f.remarks,
      };
    }

    /* ── Single unified payload ─────────────────────────────────────── */
    const payload = {
      // EV
      evStockId:     form.evStockId,
      salePrice:     Number(form.salePrice),
      orp:           Number(form.orp),
      saleDate:      form.saleDate,
      noPlate:       form.noPlate,
      cashAmount:    form.isCash ? Number(form.cashAmount) : 0,
      finance: form.isFinance ? {
        companyName: form.financeCompany,
        amount:      Number(form.financeAmount),
        financeDown:Number(form.financeDown),
        months:      Number(form.financeMonths || 0),
        emiAmount:   Number(form.emiAmount || 0),
        date:        form.financeDate || undefined,
      } : undefined,
      exchange: form.isExchange ? {
        engine:             form.exchange.engine.toUpperCase(),
        chasis:             form.exchange.chasis.toUpperCase(),
        registrationNumber: form.exchange.registrationNumber.toUpperCase(),
        model:              form.exchange.model,
        ownerName:          form.exchange.ownerName,
        phone:              form.exchange.phone,
        location:           form.exchange.location,
        vehicleType:        form.exchange.vehicleType,
        companyName:        form.exchange.companyName,
        colour:             form.exchange.colour,
        batteryType:        form.exchange.batteryType || null,
        purchasePrice:      Number(form.exchange.purchasePrice || 0),
        remarks:            form.exchange.remarks,
      } : undefined,
      expenses: {
        helmet:    Number(form.helmet    || 0),
        seatCover: Number(form.seatCover || 0),
        bikeCover: Number(form.bikeCover || 0),
        transport: Number(form.transport || 0),
        tcr:       Number(form.tcr       || 0),
        regBroker: Number(form.regBroker || 0),
      },
      gifts:          form.giftName ? [{ name: form.giftName, quantity: Number(form.giftQty) || 0 }] : [],
      charges:        { rto: Number(form.rto || 0), insurance: Number(form.insurance || 0) },
      discount:       Number(form.discount       || 0),
      couponCashback: Number(form.couponCashback || 0),
      helpBattery:form.regBroker || false,
      helpBatterySerial: form.helpBatterySerial || "",

      dueAmount:      form.dueStatus === "DUE" ? Number(form.dueAmount) : 0,
      DueRemarks:     form.dueStatus === "DUE" ? (form.dueRemarks || "") : "",
      remarks:        form.remarks,

      // Shared customer
      customerName:  form.customerName,
      fatherName:    form.fatherName,
      mobile:        mobiles,
      address:       form.address,
      village:       form.village,
      postOffice:    form.postOffice,
      policeStation: form.policeStation,
      pinCode:       form.pinCode,
      landmark:      form.landmark,
      GSTIN:         form.GSTIN,

      // Batteries array + charger object — the key change vs old multi-call approach
      batteries: batteriesPayload,
      charger:   chargerPayload,
    };

    try {
      const { data } = await api.post("/sell-bike-battery-charger", payload);

      setSteps(prev => prev.map(s => ({ ...s, status: "done" })));

      // Clear draft on success
      localStorage.removeItem(draftKey);
      setSubmitting(false);
      if (typeof onSuccess === "function") onSuccess(data);
      onClose();
    } catch (err) {
      setSteps(prev => prev.map(s => ({ ...s, status: "error" })));
      const msg = err?.response?.data?.message || err?.message || "Sale failed";
      setErrors({ _summary: [`❌ ${msg}`, "⚠️ The server has automatically rolled back all changes."] });
      setSubmitting(false);
    }
  };

  /* ══════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════ */
  return (
    <Box p={3} position="relative" sx={{
      maxHeight: "92vh", overflowY: "auto",
      background: "linear-gradient(145deg, #f8faff 0%, #fff 100%)",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": { borderRadius: 2, bgcolor: "#c5d5ea" },
    }}>

      {/* ── HEADER ──────────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#0d1b2a">Sell EV</Typography>
          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
            {bike?.vehicleType && (
              <Chip
                icon={bike.vehicleType === "E_RICKSHAW"
                  ? <ElectricRickshawIcon sx={{ fontSize: 14 }} />
                  : <TwoWheelerIcon sx={{ fontSize: 14 }} />}
                label={bike.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : "Scooty"}
                size="small"
                sx={{
                  bgcolor: bike.vehicleType === "E_RICKSHAW" ? "#FFF3E0" : "#FCE4EC",
                  color:   bike.vehicleType === "E_RICKSHAW" ? "#E65100" : "#880E4F",
                  border:  `1px solid ${bike.vehicleType === "E_RICKSHAW" ? "#FFCC80" : "#F48FB1"}`,
                }}
              />
            )}
            {bike?.model       && <Chip label={bike.model}               size="small" color="primary" variant="outlined" />}
            {bike?.companyName && <Chip label={bike.companyName}         size="small" variant="outlined" />}
            {bike?.engine      && <Chip label={`Eng: ${bike.engine}`}    size="small" variant="outlined" />}
            {bike?.chasis      && <Chip label={`CHS: ${bike.chasis}`}    size="small" variant="outlined" />}
            {bike?.colour      && <Chip label={bike.colour}              size="small" sx={{ bgcolor: "#fff3e0" }} />}
            {bike?.batteryType && <Chip label={`🔋 ${bike.batteryType}`} size="small" variant="outlined" />}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {hasDraft && (
        <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 1.5, borderRadius: 1.5, bgcolor: "#fff8e1", borderColor: "#ffc107", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="caption" fontWeight={600} color="#e65100">
            📋 Draft restored — your previously entered data has been loaded
            {batteries.length > 0 && ` (${batteries.length} batter${batteries.length > 1 ? "ies" : "y"}`}
            {batteries.length > 0 && charger ? " + charger)" : batteries.length > 0 ? ")" : charger ? " (charger)" : ""}.
          </Typography>
          <Button size="small" color="error" onClick={clearDraft} sx={{ textTransform: "none", fontSize: 11, py: 0 }}>Clear Draft</Button>
        </Paper>
      )}

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={1.5}>

        {/* ══ CUSTOMER ══ */}
        <Grid item xs={12}><Section icon={<PersonIcon fontSize="small" />} title="Customer Details" color="#1565c0" /></Grid>
        <F label="Customer Name *"   name="customerName"  value={form.customerName}  onChange={handleChange} error={errors.customerName} required md={6} />
        <F label="Father Name (C/O)" name="fatherName"    value={form.fatherName}    onChange={handleChange} md={6} />
        <F label="Mobile *"          name="mobile"        value={form.mobile}        onChange={handleChange} error={errors.mobile} required type="tel" md={6} />
        <F label="Alternate Mobile"  name="mobile2"       value={form.mobile2}       onChange={handleChange} type="tel" md={6} />
        <F label="Address"           name="address"       value={form.address}       onChange={handleChange} xs={12} md={12} />
        <F label="Village"           name="village"       value={form.village}       onChange={handleChange} md={4} />
        <F label="Post Office"       name="postOffice"    value={form.postOffice}    onChange={handleChange} md={4} />
        <F label="Police Station"    name="policeStation" value={form.policeStation} onChange={handleChange} md={4} />
        <F label="PIN Code"          name="pinCode"       value={form.pinCode}       onChange={handleChange} type="number" md={4} />
        <F label="Landmark"          name="landmark"      value={form.landmark}      onChange={handleChange} md={8} />

        {/* ══ SALE DETAILS ══ */}
        <Grid item xs={12}><Section icon={<CurrencyRupeeIcon fontSize="small" />} title="Sale Details" color="#2e7d32" /></Grid>
        <F label="Sale Price *" name="salePrice" value={form.salePrice} onChange={handleChange} error={errors.salePrice} required type="number" startAdornment={rupee} md={4} />
        <F label="ORP *"        name="orp"       value={form.orp}       onChange={handleChange} error={errors.orp}       required type="number" startAdornment={rupee} md={4} />
        <F label="GSTIN"        name="GSTIN"     value={form.GSTIN}     onChange={handleChange} md={4} />
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="Sale Date *" name="saleDate" type="date"
            value={form.saleDate} onChange={handleChange}
            error={!!errors.saleDate} helperText={errors.saleDate || ""} required
            InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField select fullWidth size="small" label="Location *" name="location"
            value={form.location} onChange={handleChange}
            error={!!errors.location} helperText={errors.location || ""} required
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>
            {EV_LOCATIONS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
          </TextField>
        </Grid>
        <F label="Number Plate" name="noPlate" value={form.noPlate} onChange={handleChange} md={4} />
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ px: 2, py: "9px", borderRadius: 1.5, height: "40px", display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: netDisplay < 0 ? "#fff5f5" : "#f1f8e9", borderColor: netDisplay < 0 ? "#ef9a9a" : "#a5d6a7" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Net (ORP − Discount − Coupon)</Typography>
            <Typography variant="body2" fontWeight={700} color={netDisplay < 0 ? "error.main" : "#2e7d32"}>₹ {netDisplay.toLocaleString("en-IN")}</Typography>
          </Paper>
        </Grid>

        {/* ══ PAYMENT MODE ══ */}
        <Grid item xs={12}><Section icon={<PaymentIcon fontSize="small" />} title="Payment Mode" color="#6a1b9a" /></Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControlLabel control={<Checkbox checked={form.isCash}     onChange={() => toggle("isCash")}     size="small" sx={{ color: "#2e7d32" }} />} label={<Typography variant="body2" fontWeight={600} color="#2e7d32">CASH</Typography>} />
            <FormControlLabel control={<Checkbox checked={form.isFinance}  onChange={() => toggle("isFinance")}  size="small" sx={{ color: "#1565c0" }} />} label={<Typography variant="body2" fontWeight={600} color="#1565c0">FINANCE</Typography>} />
            <FormControlLabel control={<Checkbox checked={form.isExchange} onChange={() => toggle("isExchange")} size="small" sx={{ color: "#e65100" }} />} label={<Typography variant="body2" fontWeight={600} color="#e65100">EXCHANGE</Typography>} />
          </Paper>
        </Grid>

        <Collapse in={form.isCash} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <F label="Cash Amount *" name="cashAmount" value={form.cashAmount} onChange={handleChange} error={errors.cashAmount} required type="number" startAdornment={rupee} md={6} />
          </Grid>
        </Collapse>

        <Collapse in={form.isFinance} style={{ width: "100%" }}>
          <Grid item xs={12}><Section icon={<AccountBalanceIcon fontSize="small" />} title="Finance Details" color="#1565c0" /></Grid>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Finance Company *" name="financeCompany"
                value={form.financeCompany} onChange={handleChange}
                error={!!errors.financeCompany} helperText={errors.financeCompany || ""}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>
                {FINANCE_COMPANIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
            </Grid>
            <F label="Finance Amount *" name="financeAmount" value={form.financeAmount} onChange={handleChange} error={errors.financeAmount} required type="number" startAdornment={rupee} md={4} />
            <F label="finance Down Amount *" name="financeDown" value={form.financeDown} onChange={handleChange} error={errors.financeDown} required type="number" startAdornment={rupee} md={4} />
            <F label="Finance Months"   name="financeMonths" value={form.financeMonths} onChange={handleChange} type="number" md={4} helperText="Loan tenure in months" />
            <F label="EMI Amount"       name="emiAmount"     value={form.emiAmount} onChange={handleChange} type="number" startAdornment={rupee} md={4} />
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Finance Date" name="financeDate" type="date"
                value={form.financeDate} onChange={handleChange} InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} />
            </Grid>
          </Grid>
        </Collapse>

        <Collapse in={form.isExchange} style={{ width: "100%" }}>
          <Grid item xs={12}><Section icon={<SwapHorizIcon fontSize="small" />} title="Exchange Vehicle Details" color="#e65100" /></Grid>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Engine No. *" name="engine" value={form.exchange.engine} onChange={handleExchange} error={!!errors.ex_engine} helperText={errors.ex_engine || "Auto-uppercase"} inputProps={{ style: { textTransform: "uppercase", letterSpacing: 1 } }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Chasis No. *" name="chasis" value={form.exchange.chasis} onChange={handleExchange} error={!!errors.ex_chasis} helperText={errors.ex_chasis || "Auto-uppercase"} inputProps={{ style: { textTransform: "uppercase", letterSpacing: 1 } }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Registration No. *" name="registrationNumber" value={form.exchange.registrationNumber} onChange={handleExchange} error={!!errors.ex_registrationNumber} helperText={errors.ex_registrationNumber || "Auto-uppercase"} inputProps={{ style: { textTransform: "uppercase", letterSpacing: 1 } }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Model *" name="model" value={form.exchange.model} onChange={handleExchange} error={!!errors.ex_model} helperText={errors.ex_model || ""} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Owner Name" name="ownerName" value={form.exchange.ownerName} onChange={handleExchange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Phone" name="phone" type="tel" value={form.exchange.phone} onChange={handleExchange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12} md={4}><TextField select fullWidth size="small" label="Location" name="location" value={form.exchange.location} onChange={handleExchange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>{EV_LOCATIONS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={4}><TextField select fullWidth size="small" label="Vehicle Type" name="vehicleType" value={form.exchange.vehicleType} onChange={handleExchange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>{EV_VEHICLE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={4}><TextField select fullWidth size="small" label="Battery Type" name="batteryType" value={form.exchange.batteryType} onChange={handleExchange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}><MenuItem value="Lithium">Lithium</MenuItem><MenuItem value="Lead">Lead</MenuItem></TextField></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Company Name" name="companyName" value={form.exchange.companyName} onChange={handleExchange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Colour" name="colour" value={form.exchange.colour} onChange={handleExchange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Purchase Price (₹)" name="purchasePrice" type="number" value={form.exchange.purchasePrice} onChange={handleExchange} InputProps={{ startAdornment: <InputAdornment position="start">{rupee}</InputAdornment> }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Remarks" name="remarks" value={form.exchange.remarks} onChange={handleExchange} multiline rows={2} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} /></Grid>
          </Grid>
        </Collapse>

        {/* ══ EXPENSES ══ */}
        <Grid item xs={12}><Section icon={<CardGiftcardIcon fontSize="small" />} title="Expenses" color="#00838f" /></Grid>
        <F label="Helmet (qty)"     name="helmet"    value={form.helmet}    onChange={handleChange} type="number" startAdornment={qtyIcon} md={4} />
        <F label="Seat Cover (qty)" name="seatCover" value={form.seatCover} onChange={handleChange} type="number" startAdornment={qtyIcon} md={4} />
        <F label="Bike Cover (qty)" name="bikeCover" value={form.bikeCover} onChange={handleChange} type="number" startAdornment={qtyIcon} md={4} />
        <F label="Transport (₹)"    name="transport" value={form.transport} onChange={handleChange} type="number" startAdornment={rupee} md={4} />
        <F label="TCR (₹)"          name="tcr"       value={form.tcr}       onChange={handleChange} type="number" startAdornment={rupee} md={4} />
        <F label="Reg. Broker (₹)"  name="regBroker" value={form.regBroker} onChange={handleChange} type="number" startAdornment={rupee} md={4} />

        {/* ══ GIFTS ══ */}
        <Grid item xs={12}><Section icon={<CardGiftcardIcon fontSize="small" />} title="Gifts" color="#ad1457" /></Grid>
        <F label="Gift Item Name" name="giftName" value={form.giftName} onChange={handleChange} md={8} />
        <F label="Qty"            name="giftQty"  value={form.giftQty}  onChange={handleChange} type="number" md={4} />

        {/* ══ CHARGES ══ */}
        <Grid item xs={12}><Section icon={<ReceiptIcon fontSize="small" />} title="Charges" color="#4527a0" /></Grid>
        <F label="RTO (₹)"       name="rto"       value={form.rto}       onChange={handleChange} type="number" startAdornment={rupee} md={6} />
        <F label="Insurance (₹)" name="insurance" value={form.insurance} onChange={handleChange} type="number" startAdornment={rupee} md={6} />

        {/* ══ DISCOUNT ══ */}
        <Grid item xs={12}><Section icon={<CurrencyRupeeIcon fontSize="small" />} title="Discount &amp; Cashback" color="#558b2f" /></Grid>
        <F label="Discount (₹)"        name="discount"       value={form.discount}       onChange={handleChange} type="number" startAdornment={rupee} md={6} />
        <F label="Coupon Cashback (₹)" name="couponCashback" value={form.couponCashback} onChange={handleChange} type="number" startAdornment={rupee} md={6} />
      <div className="col-span-12">
  <h3 className="text-lg  font-semibold mb-3" >Help Battery</h3>
</div>

<F
  label="Available"
  name="helpBattery"
  value={form.helpBattery}
  onChange={handleChange}
  type="checkbox"
  md={6}
/>

<F
  label="Battery Serial"
  name="helpBatterySerial"
  value={form.helpBatterySerial}
  onChange={handleChange}
  type="text"
  md={6}
/>
        {/* ══ PAYMENT STATUS ══ */}
        <Grid item xs={12}><Section icon={<PendingActionsIcon fontSize="small" />} title="Payment Status" color="#e65100" /></Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", bgcolor: form.dueStatus === "DUE" ? "#fff8f5" : "#f1f8e9", borderColor: form.dueStatus === "DUE" ? "#ff7043" : "#66bb6a" }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">Status:</Typography>
            <ToggleButtonGroup value={form.dueStatus} exclusive
              onChange={(_, v) => { if (v) setForm(p => ({ ...p, dueStatus: v, dueAmount: "" })); }}
              size="small" sx={{ "& .MuiToggleButton-root": { px: 2.5, py: 0.5, fontWeight: 700, fontSize: "0.75rem", borderRadius: "6px !important", border: "1px solid", mx: 0.5 } }}>
              <ToggleButton value="PAID" sx={{ borderColor: "#66bb6a !important", "&.Mui-selected": { bgcolor: "#2e7d32 !important", color: "#fff" } }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 15, mr: 0.5 }} /> PAID
              </ToggleButton>
              <ToggleButton value="DUE" sx={{ borderColor: "#ff7043 !important", "&.Mui-selected": { bgcolor: "#e65100 !important", color: "#fff" } }}>
                <PendingActionsIcon sx={{ fontSize: 15, mr: 0.5 }} /> DUE
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Grid>
        <Collapse in={form.dueStatus === "DUE"} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0.5 }}>
            <F label="Total Due Amount *" name="dueAmount"  value={form.dueAmount}  onChange={handleChange} error={errors.dueAmount} required type="number" startAdornment={rupee} md={6} helperText="Amount customer still owes" />
            <F label="Due Remarks"        name="dueRemarks" value={form.dueRemarks} onChange={handleChange} xs={12} md={6} />
          </Grid>
        </Collapse>

        {/* ══ REMARKS ══ */}
        <Grid item xs={12}><Section icon={<ReceiptIcon fontSize="small" />} title="Remarks" color="#546e7a" /></Grid>
        <F label="Remarks" name="remarks" value={form.remarks} onChange={handleChange} xs={12} md={12} multiline rows={2} />

        {/* ══════════════════════════════════════════════════════════════
            BATTERY SECTION
        ══════════════════════════════════════════════════════════════ */}
        <Grid item xs={12}>
          <Section icon={<BatteryChargingFullIcon fontSize="small" />} title="Batteries" color="#7b1fa2" />
        </Grid>

        {batteries.map((bat, idx) => (
          <Grid item xs={12} key={bat.id}>
            <Paper variant="outlined" sx={{
              p: 2, borderRadius: 2, position: "relative",
              borderColor: subErrors[`bat_${bat.id}`] ? "#ef5350" : "#ce93d8",
              bgcolor:     subErrors[`bat_${bat.id}`] ? "#fff5f5" : "#fdf5ff",
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontWeight={700} fontSize={13} color="#7b1fa2">🔋 Battery {idx + 1}</Typography>
                <IconButton size="small" onClick={() => removeBattery(bat.id)} sx={{ color: "#ef5350" }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              {subErrors[`bat_${bat.id}`] && (
                <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: 12 }}>
                  {Object.values(subErrors[`bat_${bat.id}`]).join(" · ")}
                </Alert>
              )}
              <SubSaleFields
                stockItem={bat.stockItem}
                stockType="BATTERY"
                subForm={bat.subForm}
                onChange={(e) => updateBatSubForm(bat.id, e)}
                errors={subErrors[`bat_${bat.id}`] || {}}
              />
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12}>
          {!batSearchOpen ? (
            <Button variant="outlined" startIcon={<AddCircleOutlineIcon />}
              onClick={() => setBatSearchOpen(true)}
              sx={{ borderRadius: 2, fontWeight: 700, borderColor: "#7b1fa2", color: "#7b1fa2", "&:hover": { bgcolor: "#f3e5f5", borderColor: "#7b1fa2" } }}>
              + Add Battery
            </Button>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#ce93d8", bgcolor: "#fdf5ff" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontWeight={700} fontSize={13} color="#7b1fa2">🔋 Search Battery</Typography>
                <IconButton size="small" onClick={() => setBatSearchOpen(false)}><CloseIcon fontSize="small" /></IconButton>
              </Box>
              <StockSearchTable stockType="BATTERY" onSelect={addBatterySlot} />
            </Paper>
          )}
        </Grid>

        {/* ══════════════════════════════════════════════════════════════
            CHARGER SECTION
        ══════════════════════════════════════════════════════════════ */}
        <Grid item xs={12}>
          <Section icon={<PowerIcon fontSize="small" />} title="Charger" color="#2e7d32" />
        </Grid>

        {charger && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{
              p: 2, borderRadius: 2,
              borderColor: subErrors.charger ? "#ef5350" : "#a5d6a7",
              bgcolor:     subErrors.charger ? "#fff5f5" : "#f5fff5",
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontWeight={700} fontSize={13} color="#2e7d32">⚡ Charger</Typography>
                <IconButton size="small" onClick={removeCharger} sx={{ color: "#ef5350" }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              {subErrors.charger && (
                <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: 12 }}>
                  {Object.values(subErrors.charger).join(" · ")}
                </Alert>
              )}
              <SubSaleFields
                stockItem={charger.stockItem}
                stockType="CHARGER"
                subForm={charger.subForm}
                onChange={updateCharSubForm}
                errors={subErrors.charger || {}}
              />
            </Paper>
          </Grid>
        )}

        {!charger && (
          <Grid item xs={12}>
            {!charSearchOpen ? (
              <Button variant="outlined" startIcon={<AddCircleOutlineIcon />}
                onClick={() => setCharSearchOpen(true)}
                sx={{ borderRadius: 2, fontWeight: 700, borderColor: "#2e7d32", color: "#2e7d32", "&:hover": { bgcolor: "#e8f5e9", borderColor: "#2e7d32" } }}>
                + Add Charger
              </Button>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#a5d6a7", bgcolor: "#f5fff5" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography fontWeight={700} fontSize={13} color="#2e7d32">⚡ Search Charger</Typography>
                  <IconButton size="small" onClick={() => setCharSearchOpen(false)}><CloseIcon fontSize="small" /></IconButton>
                </Box>
                <StockSearchTable stockType="CHARGER" onSelect={addCharger} />
              </Paper>
            )}
          </Grid>
        )}

        {/* ══ ERRORS ══ */}
        {errors._summary?.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setErrors(p => { const n = { ...p }; delete n._summary; return n; })}>
              <Typography fontWeight={700} fontSize={13} mb={0.5}>Please fix the following errors:</Typography>
              {errors._summary.map((msg, i) => (
                <Typography key={i} fontSize={12}>• {msg}</Typography>
              ))}
            </Alert>
          </Grid>
        )}

        {/* ══ SELL PROGRESS ══ */}
        {steps.length > 0 && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#90caf9", bgcolor: "#f3f9ff" }}>
              <Typography fontWeight={700} fontSize={13} mb={1} color="#1565c0">Sale Progress</Typography>
              <SellProgress steps={steps} />
            </Paper>
          </Grid>
        )}

      </Grid>

      {/* ══ SUBMIT BUTTON ══ */}
      <Button
        fullWidth variant="contained"
        onClick={handleSubmit}
        disabled={submitting}
        sx={{
          mt: 3, py: 1.3, borderRadius: 2, fontWeight: 700,
          fontSize: "0.95rem", letterSpacing: 1,
          background: submitting ? "#bdbdbd" : "linear-gradient(135deg, #1565c0, #42a5f5)",
          boxShadow: "0 4px 14px rgba(21,101,192,0.35)",
          "&:hover": { background: submitting ? "#bdbdbd" : "linear-gradient(135deg, #0d47a1, #1976d2)" },
        }}>
        {submitting
          ? <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} sx={{ color: "#fff" }} />
              <span>Processing…</span>
            </Box>
          : `✅ Confirm Sale${batteries.length || charger ? ` (EV${batteries.length ? ` + ${batteries.length} Battery` : ""}${charger ? " + Charger" : ""})` : ""}`}
      </Button>
    </Box>
  );
};

export default SellBikeForm;